import React, { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Clock, Mic, Timer, Calendar, Zap } from "lucide-react";
import { twMerge } from "tailwind-merge";

export const AnimatedLogoRolodex = ({ size = "large" }) => {
  const isSmall = size === "small";
  const iconSize = isSmall ? "w-6 h-6" : "w-16 h-16";
  
  return (
    <div className="flex items-center justify-center">
      <LogoRolodex
        size={size}
        items={[
          <LogoItem key={1} className="bg-gradient-to-br from-cyan-400 to-cyan-600 text-white" size={size}>
            <Clock className={iconSize} />
          </LogoItem>,
          <LogoItem key={2} className="bg-gradient-to-br from-pink-400 to-rose-500 text-white" size={size}>
            <Mic className={iconSize} />
          </LogoItem>,
          <LogoItem key={3} className="bg-gradient-to-br from-blue-500 to-indigo-600 text-white" size={size}>
            <Timer className={iconSize} />
          </LogoItem>,
          <LogoItem key={4} className="bg-gradient-to-br from-green-400 to-teal-500 text-white" size={size}>
            <Calendar className={iconSize} />
          </LogoItem>,
          <LogoItem key={5} className="bg-gradient-to-br from-yellow-400 to-orange-500 text-white" size={size}>
            <Zap className={iconSize} />
          </LogoItem>,
        ]}
      />
    </div>
  );
};

const DELAY_IN_MS = 2500;
const TRANSITION_DURATION_IN_SECS = 1.5;

const LogoRolodex = ({ items, size = "large" }) => {
  const intervalRef = useRef(null);
  const [index, setIndex] = useState(0);
  
  const isSmall = size === "small";
  const containerClass = isSmall 
    ? "h-10 w-10 rounded-lg border border-cyan-400/30" 
    : "h-44 w-60 rounded-xl border-2 border-cyan-500/30";

  useEffect(() => {
    intervalRef.current = setInterval(() => {
      setIndex((pv) => pv + 1);
    }, DELAY_IN_MS);

    return () => {
      clearInterval(intervalRef.current || undefined);
    };
  }, []);

  return (
    <div
      style={{
        transform: isSmall ? "none" : "rotateY(-20deg)",
        transformStyle: "preserve-3d",
      }}
      className={`relative z-0 shrink-0 bg-white shadow-lg ${containerClass}`}
    >
      <AnimatePresence mode="sync">
        <motion.div
          style={{
            y: "-50%",
            x: "-50%",
            clipPath: "polygon(0 0, 100% 0, 100% 50%, 0 50%)",
            zIndex: -index,
            backfaceVisibility: "hidden",
          }}
          key={index}
          transition={{
            duration: TRANSITION_DURATION_IN_SECS,
            ease: "easeInOut",
          }}
          initial={{ rotateX: "0deg" }}
          animate={{ rotateX: "0deg" }}
          exit={{ rotateX: "-180deg" }}
          className="absolute left-1/2 top-1/2"
        >
          {items[index % items.length]}
        </motion.div>
        <motion.div
          style={{
            y: "-50%",
            x: "-50%",
            clipPath: "polygon(0 50%, 100% 50%, 100% 100%, 0 100%)",
            zIndex: index,
            backfaceVisibility: "hidden",
          }}
          key={(index + 1) * 2}
          initial={{ rotateX: "180deg" }}
          animate={{ rotateX: "0deg" }}
          exit={{ rotateX: "0deg" }}
          transition={{
            duration: TRANSITION_DURATION_IN_SECS,
            ease: "easeInOut",
          }}
          className="absolute left-1/2 top-1/2"
        >
          {items[index % items.length]}
        </motion.div>
      </AnimatePresence>

      <hr
        style={{
          transform: "translateZ(1px)",
        }}
        className={`absolute left-0 right-0 top-1/2 z-[999999999] -translate-y-1/2 border-t-2 border-gray-200 ${isSmall ? 'hidden' : ''}`}
      />
    </div>
  );
};

const LogoItem = ({ children, className, size = "large" }) => {
  const isSmall = size === "small";
  const sizeClass = isSmall 
    ? "h-10 w-10 place-content-center rounded-lg text-xl shadow-md" 
    : "h-36 w-52 place-content-center rounded-lg text-6xl shadow-lg";
  
  return (
    <div
      className={twMerge(
        `grid ${sizeClass}`,
        className
      )}
    >
      {children}
    </div>
  );
};

export default AnimatedLogoRolodex;
